/**
 * A match counts GAMES won over several games: first to `winsNeeded` wins within at most `maxGames`
 * games. A weekend of play stacks two of them (see tournament.ts): a SESSION is first to 10 game wins
 * within 15 games, and a SERIES is first to 3 sessions within 5. It is pure and game-agnostic:
 * the keys are whatever wins a game, a side in Whist (`'NS' | 'EW'`) or a player in Pirate Bridge
 * (`'N' | 'E' | 'S' | 'W'`). It knows nothing about storage; see `serializeMatch` / `parseMatch`.
 */
export interface MatchOptions {
  readonly winsNeeded: number;
  readonly maxGames: number;
}

/** A session: first to 10 game wins, at most 15 games (so a session can end level and is then drawn). */
export const DEFAULT_SESSION: MatchOptions = { winsNeeded: 10, maxGames: 15 };
/** A series of sessions: first to 3 session wins, at most 5 (with two sides that always produces a winner). */
export const DEFAULT_SERIES: MatchOptions = { winsNeeded: 3, maxGames: 5 };

export interface Match<Key extends string> {
  readonly options: MatchOptions;
  readonly keys: readonly Key[];
  readonly wins: Readonly<Record<Key, number>>;
  readonly games: number;
}

export type MatchStatus<Key extends string> =
  | { readonly state: 'playing' }
  /** One key has more wins than any other when the match ends. */
  | { readonly state: 'won'; readonly winner: Key }
  /** The match ended level: after `maxGames` games, several keys share the most wins. */
  | { readonly state: 'drawn'; readonly leaders: readonly Key[] };

export function newMatch<Key extends string>(keys: readonly Key[], options: MatchOptions = DEFAULT_SESSION): Match<Key> {
  if (!Number.isInteger(options.winsNeeded) || options.winsNeeded < 1) throw new Error(`winsNeeded must be a whole number of at least 1, not ${options.winsNeeded}`);
  if (!Number.isInteger(options.maxGames) || options.maxGames < options.winsNeeded) throw new Error(`maxGames must be a whole number of at least winsNeeded (${options.winsNeeded}), not ${options.maxGames}`);
  if (keys.length < 2 || new Set(keys).size !== keys.length) throw new Error('a match needs at least two distinct keys');
  return { options, keys, wins: Object.fromEntries(keys.map((k) => [k, 0])) as Record<Key, number>, games: 0 };
}

const mostWins = <Key extends string>(m: Match<Key>): { best: number; leaders: Key[] } => {
  const best = Math.max(...m.keys.map((k) => m.wins[k]));
  return { best, leaders: m.keys.filter((k) => m.wins[k] === best) };
};

export function matchStatus<Key extends string>(m: Match<Key>): MatchStatus<Key> {
  const { best, leaders } = mostWins(m);
  const over = best >= m.options.winsNeeded || m.games >= m.options.maxGames;
  if (!over) return { state: 'playing' };
  // Several keys at the top when the match is over (a shared win, or the games ran out): keep playing
  // only while games remain, otherwise it is a draw.
  if (leaders.length === 1) return { state: 'won', winner: leaders[0] as Key };
  return m.games >= m.options.maxGames ? { state: 'drawn', leaders } : { state: 'playing' };
}

/**
 * Record one finished game. `winners` is the key that won it, or several for a shared win (each gets a
 * win); an empty list is not a result and is refused.
 */
export function recordGame<Key extends string>(m: Match<Key>, winners: readonly Key[]): Match<Key> {
  if (matchStatus(m).state !== 'playing') throw new Error('the match is already over');
  if (winners.length === 0) throw new Error('a game has at least one winner');
  for (const w of winners) if (!m.keys.includes(w)) throw new Error(`not a player in this match: ${w}`);
  const wins = { ...m.wins } as Record<Key, number>;
  for (const w of new Set(winners)) wins[w] += 1;
  return { ...m, wins, games: m.games + 1 };
}

/** JSON for local storage. */
export const serializeMatch = <Key extends string>(m: Match<Key>): string => JSON.stringify({ v: 1, ...m });

/**
 * Read a saved match. Anything absent, malformed or inconsistent (wrong version, unknown or missing
 * keys, negative or fractional counts, more wins than games could give, a stored match for different
 * `keys`) returns null, and the caller starts a fresh match.
 */
export function parseMatch<Key extends string>(raw: string | null | undefined, keys: readonly Key[]): Match<Key> | null {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as Record<string, unknown>;
    if (d.v !== 1 || typeof d.games !== 'number' || !Number.isInteger(d.games) || d.games < 0) return null;
    const o = d.options as Partial<MatchOptions> | undefined;
    const w = d.wins as Record<string, unknown> | undefined;
    if (!o || !w || !Array.isArray(d.keys)) return null;
    if (d.keys.length !== keys.length || !keys.every((k, i) => d.keys && (d.keys as unknown[])[i] === k)) return null;
    const base = newMatch(keys, o as MatchOptions); // validates the options
    const wins = {} as Record<Key, number>;
    for (const k of keys) {
      const n = w[k];
      if (typeof n !== 'number' || !Number.isInteger(n) || n < 0 || n > d.games) return null;
      wins[k] = n;
    }
    if (Object.keys(w).length !== keys.length) return null;
    if (d.games > base.options.maxGames) return null;
    return { ...base, wins, games: d.games };
  } catch {
    return null;
  }
}
